import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import CustomerCard from '../components/CustomerCard';
import { useTranslation } from 'react-i18next';

const Dashboard = ({ recentCustomers = [], loading: parentLoading }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stats state
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalPoints: 0,
    totalTransactions: 0,
    averagePoints: 0
  });
  
  // Monthly points data for chart
  const [monthlyPointsData, setMonthlyPointsData] = useState([]);
  
  // Calculate max points for proper scaling
  const maxPoints = monthlyPointsData.length > 0 
    ? Math.max(...monthlyPointsData.map(item => item.points || 0)) 
    : 30000; // Fallback if no data
  
  // Check if we have actual data with values > 0
  const hasPointsData = monthlyPointsData.length > 0 && 
    monthlyPointsData.some(item => (item.points || 0) > 0);

  // For RTL layouts, we need to reverse the order of the months for proper display
  const displayData = isRTL && monthlyPointsData.length > 0 
    ? [...monthlyPointsData].reverse() 
    : monthlyPointsData;
  
  // Fetch transactions directly from Firestore, similar to TransactionsPage
  const fetchTransactionsDirectly = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get Firestore instance
      const db = getFirestore();
      
      // Access transactions collection directly
      const transactionsRef = collection(db, "transactions");
      const q = query(transactionsRef, orderBy("timestamp", "desc"), limit(100));
      
      console.log("Dashboard: Executing direct Firestore query for transactions...");
      const querySnapshot = await getDocs(q);
      
      console.log(`Dashboard: Query returned ${querySnapshot.size} transactions`);
      
      if (querySnapshot.empty) {
        console.warn("Dashboard: The transactions collection appears to be empty");
        calculateStats([]);
        calculateMonthlyData([]);
        return;
      }
      
      // Process the results
      const transactions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        transactions.push({
          id: doc.id,
          ...data,
          // Ensure amount is a number
          amount: Number(data.amount) || 0,
          // Ensure points is a number
          points: Number(data.points) || 0
        });
      });
      
      // Calculate stats and monthly data
      calculateStats(transactions);
      calculateMonthlyData(transactions);
      
    } catch (err) {
      console.error("Dashboard: Error in direct Firestore access:", err);
      setError(t('dashboard.errors.fetchingFailed', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate stats from transactions
  const calculateStats = (transactions) => {
    if (!transactions || transactions.length === 0) {
      setStats({
        totalCustomers: 0,
        totalPoints: 0,
        totalTransactions: 0,
        averagePoints: 0
      });
      return;
    }

    // Count unique customers
    const uniqueCustomers = new Set();
    let totalPoints = 0;
    let netPoints = 0; // Points after redemptions
    
    transactions.forEach(transaction => {
      if (transaction.customerId) {
        uniqueCustomers.add(transaction.customerId);
      }
      
      // Add points (positive for earnings, negative for redemptions)
      totalPoints += Math.abs(transaction.points || 0);
      netPoints += transaction.points || 0;
    });
    
    const totalCustomers = uniqueCustomers.size;
    const averagePoints = totalCustomers > 0 ? Math.round(netPoints / totalCustomers) : 0;
    
    setStats({
      totalCustomers,
      totalPoints, // Total points (absolute value)
      totalTransactions: transactions.length,
      averagePoints // Average net points per customer
    });
  };
  
  // Calculate monthly points data for chart
  const calculateMonthlyData = (transactions) => {
    if (!transactions || transactions.length === 0) {
      setMonthlyPointsData([]); // Fixed: using setMonthlyPointsData instead of setMonthlyData
      return;
    }
    
    // Group by month
    const monthlyData = {};
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get date 6 months ago
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5); // 6 months including current month
    
    transactions.forEach(transaction => {
      if (transaction.timestamp) {
        try {
          const date = transaction.timestamp.toDate();
          if (date >= sixMonthsAgo) {
            const monthKey = monthNames[date.getMonth()];
            
            if (!monthlyData[monthKey]) {
              monthlyData[monthKey] = 0;
            }
            
            // Add points (only positive values for the chart)
            if (transaction.points > 0) {
              monthlyData[monthKey] += transaction.points;
            }
          }
        } catch (err) {
          console.error("Error processing transaction for monthly points:", err);
        }
      }
    });
    
    // Convert to array format for chart and ensure all months appear
    const result = [];
    const currentMonth = new Date().getMonth();
    
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12; // Handle wrapping around to previous year
      const month = monthNames[monthIndex];
      result.push({
        month: month,
        points: monthlyData[month] || 0
      });
    }
    
    setMonthlyPointsData(result);
  };
  
  // Fetch data on component mount
  useEffect(() => {
    fetchTransactionsDirectly();
    
    // Fetch data every 5 minutes
    const interval = setInterval(() => {
      fetchTransactionsDirectly();
    }, 300000); // 5 minutes
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="dashboard-page">
      <div className="page-title">
        <h2>{t('dashboard.title')}</h2>
        <p className="subtitle">{t('dashboard.subtitle')}</p>
      </div>
      
      {loading || parentLoading ? (
        <div className="loading-container">{t('common.loading')}</div>
      ) : (
        <>
          <div className="stats-grid">
            <StatCard 
              title={t('dashboard.stats.totalCustomers')} 
              value={stats.totalCustomers || 0}
              icon="👥" 
              color="blue"
            />
          </div>
          
          <div className="dashboard-grid">
            <ChartCard title={t('dashboard.charts.monthlyPoints')}>
              {hasPointsData ? (
                <div className="chart-placeholder">
                  <div className={`chart-bars ${isRTL ? 'chart-bars-rtl' : ''}`}>
                    {displayData.map((item, index) => {
                      // Calculate percentage height safely
                      const heightPercentage = maxPoints > 0 
                        ? ((item.points || 0) / maxPoints) * 100 
                        : 0;
                        
                      return (
                        <div className="chart-bar-container" key={index}>
                          <div 
                            className="chart-bar" 
                            style={{ 
                              height: `${heightPercentage}%`,
                              // Make bars at least 5px tall if they have any value
                              minHeight: heightPercentage > 0 ? '5px' : '0'
                            }}
                          ></div>
                          <div className="chart-label">{item.month}</div>
                          <div className="chart-value">
                            {isRTL 
                              ? item.points?.toLocaleString('en') || 0 
                              : item.points?.toLocaleString() || 0}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="empty-chart-state">
                  <p>{t('dashboard.charts.noData')}</p>
                  <p>{t('dashboard.charts.startIssuing')}</p>
                </div>
              )}
            </ChartCard>
            
            <div className="recent-customers-card">
              <div className="customers-header">
                <h3 className="chart-title">{t('dashboard.recentCustomers')}</h3>
              </div>
              {recentCustomers.length > 0 ? (
                <div className="customers-list">
                  {recentCustomers.map(customer => (
                    <CustomerCard 
                      key={customer.id} 
                      customer={customer} 
                      onSelect={() => {}} 
                      compact={true} // Use compact mode for dashboard
                      isRTL={isRTL}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-state">{t('dashboard.empty')}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;